const FALLBACK_TIME_ZONES = [
  'UTC',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'America/Mexico_City',
  'America/Bogota',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Tokyo',
  'Australia/Sydney'
];

const POPULAR_TIME_ZONES = [
  'UTC',
  'Europe/London',
  'Europe/Lisbon',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Asia/Tokyo'
];

const timeZoneLabelCache = new Map();
const timeZoneOffsetCache = new Map();

const getTimeZoneOffsetMinutes = (timeZone) => {
  if (!timeZone) {
    return 0;
  }

  if (timeZoneOffsetCache.has(timeZone)) {
    return timeZoneOffsetCache.get(timeZone);
  }

  let offsetMinutes = 0;

  if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'longOffset',
        hour: '2-digit',
        minute: '2-digit',
      });
      const timeZonePart = formatter.formatToParts(new Date()).find((part) => part.type === 'timeZoneName');
      const rawOffset = timeZonePart?.value?.replace('GMT', '').replace('UTC', '').trim();

      if (!rawOffset) {
        offsetMinutes = 0;
      } else {
        const match = rawOffset.match(/^([+-])(\d{1,2})(?::?(\d{2}))?$/);
        if (match) {
          const [, sign, hours, minutes] = match;
          const totalMinutes = Number(hours) * 60 + Number(minutes || '0');
          offsetMinutes = sign === '-' ? -totalMinutes : totalMinutes;
        }
      }
    } catch {
      offsetMinutes = 0;
    }
  }

  timeZoneOffsetCache.set(timeZone, offsetMinutes);
  return offsetMinutes;
};

const sortTimeZonesByOffset = (timeZones) => (
  [...timeZones].sort((left, right) => {
    const offsetDifference = getTimeZoneOffsetMinutes(left) - getTimeZoneOffsetMinutes(right);
    if (offsetDifference !== 0) {
      return offsetDifference;
    }

    return left.localeCompare(right);
  })
);

export const getDefaultTimeZone = () => {
  if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserTimeZone) {
      return browserTimeZone;
    }
  }

  return 'UTC';
};

export const getTimeZoneOptions = () => {
  const defaultTimeZone = getDefaultTimeZone();
  const supportedTimeZones = typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : FALLBACK_TIME_ZONES;

  const timeZoneSet = new Set(supportedTimeZones);
  if (defaultTimeZone) {
    timeZoneSet.add(defaultTimeZone);
  }

  return sortTimeZonesByOffset([...timeZoneSet]);
};

export const getPopularTimeZoneOptions = () => {
  const availableTimeZones = new Set(getTimeZoneOptions());
  const currentTimeZone = getDefaultTimeZone();
  const popularTimeZones = POPULAR_TIME_ZONES.filter((timeZone) => availableTimeZones.has(timeZone));

  if (currentTimeZone && availableTimeZones.has(currentTimeZone) && !popularTimeZones.includes(currentTimeZone)) {
    popularTimeZones.unshift(currentTimeZone);
  }

  return sortTimeZonesByOffset(popularTimeZones.slice(0, 10));
};

const normalizeOffsetLabel = (offsetLabel) => {
  if (!offsetLabel || offsetLabel === 'GMT' || offsetLabel === 'UTC') {
    return 'UTC +00:00';
  }

  const normalized = offsetLabel.replace('GMT', 'UTC').replace('UTC', '').trim();
  if (/^[+-]\d{2}:\d{2}$/.test(normalized)) {
    return `UTC ${normalized}`;
  }

  if (/^[+-]\d{1,2}$/.test(normalized)) {
    const sign = normalized[0];
    const hours = normalized.slice(1).padStart(2, '0');
    return `UTC ${sign}${hours}:00`;
  }

  return offsetLabel.replace('GMT', 'UTC');
};

export const getTimeZoneDisplayLabel = (timeZone) => {
  if (!timeZone) {
    return '';
  }

  if (timeZoneLabelCache.has(timeZone)) {
    return timeZoneLabelCache.get(timeZone);
  }

  let offsetLabel = 'UTC +00:00';

  if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'longOffset',
        hour: '2-digit',
        minute: '2-digit',
      });
      const timeZonePart = formatter.formatToParts(new Date()).find((part) => part.type === 'timeZoneName');
      offsetLabel = normalizeOffsetLabel(timeZonePart?.value);
    } catch {
      offsetLabel = 'UTC +00:00';
    }
  }

  const displayLabel = `${offsetLabel} · ${timeZone}`;
  timeZoneLabelCache.set(timeZone, displayLabel);
  return displayLabel;
};
